import { memo, Fragment, FC } from 'react';
import { v1 as uuid } from 'uuid';
import {
  getIn,
  Field,
  connect as connectFormik,
  FieldProps,
  FormikProps,
  FieldArray,
} from 'formik';
import { inputTypes } from './Messages';
import Input from 'components/Input';
import Button from 'components/Button';
import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import {
  FieldBox,
  QuestionFields,
  Delete,
  OptionListContainer,
} from './styles';
import HoverIcon from '../../../../../../HoverIcon/HoverIcon';
import { IFormValues, IMultipleAnswerField } from 'store/survey/surveyTypes';
import i18n from '../../../../../../../i18n';
import { generateRandomString } from '../../../../../../../utils/string';

interface IFormikConnect {
  formik: FormikProps<IFormValues>;
}

const getOptions = (selectedOptions: IOption[]) => {
  const result = selectedOptions.map((option) => {
    return option.label;
  });
  return result.length > 0 ? result : null;
};

const isDigitString = (value: string): boolean => {
  return /^\d+$/.test(value);
};

const MultipleAnswerFieldsEditor: FC<IFormikConnect> = ({
  formik: { values },
}) => {
  const InputTypeOptions = [
    { value: 'date', label: i18n.t('date', { ns: 'survey' }) as string },
    { value: 'text', label: i18n.t('text', { ns: 'survey' }) as string },
    { value: 'time', label: i18n.t('time', { ns: 'survey' }) as string },
    { value: 'number', label: i18n.t('number', { ns: 'survey' }) as string },
    {
      value: 'duration',
      label: i18n.t('duration', { ns: 'survey' }) as string,
    },
  ];

  const questionId = values.data.id;
  const maxAnswers = getIn(values, ['data', 'maxAnswers']);
  const questionFields: any = getIn(values, ['data', 'questionFields']);
  return (
    <Fragment>
      <QuestionFields>
        <Field name="data.maxAnswers">
          {({ field }: FieldProps) => (
            <Input
              {...field}
              id="maxAnswers"
              type="number"
              name="data.maxAnswers"
              label={i18n.t('maxAnswers', { ns: 'survey' }) as string}
              value={maxAnswers ? maxAnswers.toString() : ''}
              placeholder={
                i18n.t('withoutLimitation', {
                  ns: 'survey',
                }) as string
              }
            />
          )}
        </Field>

        <FieldArray name="data.questionFields">
          {({ push, remove, move }) => (
            <div>
              <Button
                onClick={() =>
                  push({
                    key: `${generateRandomString(10)}`,
                    popup: false,
                    title: '',
                    hidden: false,
                    options: null,
                    required: false,
                    inputType: 'text',
                    withValue: false,
                  })
                }
              >
                {
                  i18n.t('addField', {
                    ns: 'survey',
                  }) as string
                }
              </Button>
              {questionFields &&
                questionFields.map(
                  (questionField: IMultipleAnswerField, index: number) => {
                    const textInputId = uuid();
                    const requiredInputId = uuid();
                    const popUpInputId = uuid();
                    const withValueInputId = uuid();
                    const moveUp = () => {
                      move(index, index - 1);
                    };
                    const moveDown = () => {
                      move(index, index + 1);
                    };
                    return (
                      <FieldBox key={index}>
                        <span style={{ display: 'block' }}>
                          <Delete
                            type="x"
                            color="red"
                            onClick={() => remove(index)}
                          />
                          {index > 0 && (
                            <HoverIcon
                              color="blue"
                              type="arrow-up"
                              onClick={moveUp}
                            />
                          )}
                          {questionFields.length > index + 1 && (
                            <HoverIcon
                              color="blue"
                              type="arrow-down"
                              onClick={moveDown}
                            />
                          )}
                        </span>

                        <Field name={`data.questionFields.${index}.title`}>
                          {({ field }: FieldProps) => (
                            <Input
                              {...field}
                              id={textInputId}
                              type="text"
                              label={
                                i18n.t('fieldTitle', {
                                  ns: 'survey',
                                }) as string
                              }
                              placeholder={
                                i18n.t('fieldTitle', {
                                  ns: 'survey',
                                }) as string
                              }
                            />
                          )}
                        </Field>
                        <div className="flex flex-wrap gap-5 items-center my-3">
                          <Field name={`data.questionFields.${index}.required`}>
                            {({ field }: FieldProps) => (
                              <Input
                                {...field}
                                id={requiredInputId}
                                type="checkbox"
                                checked={questionField.required || false}
                                label={
                                  i18n.t('required', {
                                    ns: 'survey',
                                  }) as string
                                }
                                className="col s6"
                                placeholder={
                                  i18n.t('required', {
                                    ns: 'survey',
                                  }) as string
                                }
                              />
                            )}
                          </Field>

                          <Field name={`data.questionFields.${index}.popup`}>
                            {({ field }: FieldProps) => (
                              <Input
                                {...field}
                                id={popUpInputId}
                                type="checkbox"
                                checked={questionField.popup || false}
                                label={
                                  i18n.t('popup', {
                                    ns: 'survey',
                                  }) as string
                                }
                                className="col s6"
                                placeholder={
                                  i18n.t('popup', {
                                    ns: 'survey',
                                  }) as string
                                }
                              />
                            )}
                          </Field>

                          <Field
                            name={`data.questionFields.${index}.withValue`}
                          >
                            {({ field }: FieldProps) => (
                              <Input
                                {...field}
                                id={withValueInputId}
                                type="checkbox"
                                checked={questionField.withValue || false}
                                label={
                                  i18n.t('withValue', {
                                    ns: 'survey',
                                  }) as string
                                }
                                className="col s6"
                                placeholder={
                                  i18n.t('withValue', {
                                    ns: 'survey',
                                  }) as string
                                }
                              />
                            )}
                          </Field>

                          <Field
                            name={`data.questionFields.${index}.viewCondition`}
                          >
                            {({ field, form }: FieldProps) => (
                              <Input
                                {...field}
                                id={uuid()}
                                type="checkbox"
                                checked={!!questionField?.viewCondition}
                                label={
                                  i18n.t('viewCondition', {
                                    ns: 'survey',
                                  }) as string
                                }
                                onChange={(e) => {
                                  form.setFieldValue(
                                    `data.questionFields.${index}.viewCondition`,
                                    e.target.checked
                                      ? {
                                          refQuestionId: questionId,
                                          refQuestionPart: '',
                                        }
                                      : null
                                  );
                                }}
                                className="col s6"
                                placeholder={
                                  i18n.t('viewCondition', {
                                    ns: 'survey',
                                  }) as string
                                }
                              />
                            )}
                          </Field>
                        </div>
                        <div className="flex flex-wrap gap-5 items-center">
                          {questionField.viewCondition && (
                            <div style={{ width: '49%' }}>
                              <Field>
                                {({ form, field }: FieldProps) => (
                                  <Select
                                    {...field}
                                    label={
                                      i18n.t('fieldCondition', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                    values={questionFields
                                      ?.slice(0, index)
                                      ?.filter(
                                        (f: IMultipleAnswerField) =>
                                          f.key &&
                                          questionField.viewCondition
                                            ?.refQuestionPart === f.key
                                      )
                                      ?.map((f: IMultipleAnswerField) => {
                                        return {
                                          value: f.key || '',
                                          label: f.title || '',
                                        };
                                      })
                                      ?.filter((opt: IOption) => opt.value)}
                                    onChangeValues={(values: IOption[]) => {
                                      form.setFieldValue(
                                        `data.questionFields.${index}.viewCondition`,
                                        {
                                          refQuestionId: questionId,
                                          refQuestionPart: values?.[0]?.value,
                                        }
                                      );
                                    }}
                                    options={questionFields
                                      ?.slice(0, index)
                                      ?.filter(
                                        (f: IMultipleAnswerField) => f.key
                                      )
                                      ?.map((f: IMultipleAnswerField) => {
                                        return {
                                          value: f.key || '',
                                          label: f.title || '',
                                        };
                                      })
                                      ?.filter((opt: IOption) => opt.value)}
                                  />
                                )}
                              </Field>
                            </div>
                          )}

                          {questionField.viewCondition &&
                            questionField.viewCondition.refQuestionPart && (
                              <div style={{ width: '49%' }}>
                                <Field
                                  name={`data.questionFields.${index}.viewCondition.questionValues`}
                                >
                                  {({ form, field }: FieldProps) => (
                                    <Select
                                      {...field}
                                      label={
                                        i18n.t('fieldConditionChoices', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                      values={questionField.viewCondition?.questionValues?.map(
                                        (val: string) => {
                                          return { value: val, label: val };
                                        }
                                      )}
                                      onChangeValues={(values: IOption[]) => {
                                        form.setFieldValue(
                                          `data.questionFields.${index}.viewCondition.questionValues`,
                                          values.map((val) => val.value)
                                        );
                                      }}
                                      options={
                                        questionFields
                                          ?.find(
                                            (qf: IMultipleAnswerField) =>
                                              qf.key ===
                                              questionField.viewCondition
                                                ?.refQuestionPart
                                          )
                                          ?.options?.map((option: string) => {
                                            return {
                                              value: option,
                                              label: option,
                                            };
                                          }) || []
                                      }
                                      multiChoice={true}
                                    />
                                  )}
                                </Field>
                              </div>
                            )}
                        </div>
                        <Field name={`data.questionFields.${index}.inputType`}>
                          {({ form }: FieldProps) => (
                            <Select
                              label={
                                i18n.t('inputType', {
                                  ns: 'survey',
                                }) as string
                              }
                              values={
                                questionField.inputType
                                  ? [
                                      {
                                        value: questionField.inputType,
                                        label:
                                          inputTypes[questionField.inputType],
                                      },
                                    ]
                                  : []
                              }
                              options={InputTypeOptions}
                              placeholder={
                                i18n.t('selectAnInputType', {
                                  ns: 'survey',
                                }) as string
                              }
                              onChangeValues={(selectedType) => {
                                form.setFieldValue(
                                  `data.questionFields.${index}.inputType`,
                                  selectedType[0].value
                                );
                              }}
                            />
                          )}
                        </Field>

                        <Field name={`data.questionFields.${index}.options`}>
                          {({ form }: FieldProps) => (
                            <Select
                              label={
                                i18n.t('options', {
                                  ns: 'survey',
                                }) as string
                              }
                              values={
                                questionField.options
                                  ? questionField.options.reduce(
                                      (list: IOption[], optionText: string) => {
                                        list.push({
                                          label: optionText,
                                          value: optionText,
                                        });
                                        return list;
                                      },
                                      []
                                    )
                                  : []
                              }
                              options={[]}
                              letAddChip={true}
                              multiChoice={true}
                              placeholder={
                                i18n.t('addOptionIfItIsNecessary', {
                                  ns: 'survey',
                                }) as string
                              }
                              onChangeValues={(selectedTags) => {
                                form.setFieldValue(
                                  `data.questionFields.${index}.options`,
                                  getOptions(selectedTags)
                                );
                              }}
                            />
                          )}
                        </Field>

                        <OptionListContainer>
                          {questionField.withValue &&
                            questionField.options
                              ?.filter(
                                (option: string) => !isDigitString(option)
                              )
                              .map((option: string) => {
                                return (
                                  <div
                                    key={`data.questionFields.${index}.optionValues.${option}`}
                                  >
                                    <Field
                                      name={`data.questionFields.${index}.optionValues.${option}`}
                                      className="flex"
                                    >
                                      {({ form, field }: FieldProps) => (
                                        <Input
                                          {...field}
                                          id={uuid()}
                                          type="number"
                                          label={option}
                                          value={
                                            questionField.valueMap?.[option] ||
                                            0
                                          }
                                          onChange={(e: any) =>
                                            form.setFieldValue(
                                              `data.questionFields.${index}.valueMap`,
                                              questionField.valueMap
                                                ? {
                                                    ...questionField.valueMap,
                                                    [option]: e.target.value,
                                                  }
                                                : {
                                                    [option]: e.target.value,
                                                  }
                                            )
                                          }
                                          placeholder={
                                            i18n.t('optionValue', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                        />
                                      )}
                                    </Field>
                                  </div>
                                );
                              })}
                        </OptionListContainer>

                        <Field name={`data.questionFields.${index}.protection`}>
                          {({ form }: FieldProps) => (
                            <Select
                              isRequired={true}
                              placeholder={
                                i18n.t('protection', {
                                  ns: 'survey',
                                }) as string
                              }
                              label={
                                i18n.t('protection', {
                                  ns: 'survey',
                                }) as string
                              }
                              values={[
                                {
                                  value: questionField.protection || 'null',
                                  label: i18n.t(
                                    `${
                                      'protection_' +
                                      (questionField.protection || 'null')
                                    }`,
                                    { ns: 'survey' }
                                  ) as string,
                                },
                              ]}
                              options={[
                                {
                                  value: 'null',
                                  label: i18n.t('protection_null', {
                                    ns: 'survey',
                                  }) as string,
                                },
                                {
                                  value: 'Edit',
                                  label: i18n.t('protection_Edit', {
                                    ns: 'survey',
                                  }) as string,
                                },
                                {
                                  value: 'View',
                                  label: i18n.t('protection_View', {
                                    ns: 'survey',
                                  }) as string,
                                },
                              ]}
                              allowRemove={false}
                              onChangeValues={(values) => {
                                const value =
                                  values[0].value === 'null'
                                    ? null
                                    : values[0].value;
                                form.setFieldValue(
                                  `data.questionFields.${index}.protection`,
                                  value
                                );
                              }}
                            />
                          )}
                        </Field>

                        <Field name={`data.questionFields.${index}.flags`}>
                          {({ form }: FieldProps) => (
                            <Select
                              label={
                                i18n.t('flags', { ns: 'survey' }) as string
                              }
                              values={
                                questionField.flags
                                  ? questionField.flags.reduce(
                                      (list: IOption[], optionText: string) => {
                                        list.push({
                                          label: optionText,
                                          value: optionText,
                                        });
                                        return list;
                                      },
                                      []
                                    )
                                  : []
                              }
                              options={[]}
                              letAddChip={true}
                              multiChoice={true}
                              placeholder={
                                i18n.t('flags', { ns: 'survey' }) as string
                              }
                              onChangeValues={(selectedTags) => {
                                form.setFieldValue(
                                  `data.questionFields.${index}.flags`,
                                  getOptions(selectedTags)
                                );
                              }}
                            />
                          )}
                        </Field>
                      </FieldBox>
                    );
                  }
                )}
            </div>
          )}
        </FieldArray>
      </QuestionFields>
    </Fragment>
  );
};

export default memo(
  connectFormik<any, IFormValues>(MultipleAnswerFieldsEditor)
);
